// Before & After Bridge — React port of kula-create's before-after-bridge.html.
//
// Pedagogy: surface assumptions, then shift them. Three phases:
//   1. Before — learner answers prompts (text or scale)
//   2. Content — read carefully-selected evidence
//   3. After — answer the same prompts (reworded). See the gap.
//
// Fires onActivityResponse ONCE on completion with the full
//   { before: {[promptId]: value}, after: {[promptId]: value} }
// payload. The shift is in the diff between the two — one row per slide
// captures the whole learning event.

import { useState, useCallback, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { BeforeAfterBridgeSlideData, Theme, ExperienceConclusion } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { Reveal } from '../../shared/Reveal';
import { ExperienceClose } from '../../shared/ExperienceClose';
import type { ActivityResponsePayload } from '../../../../../types';
import { AuroraBeforeAfterBridge } from './AuroraBeforeAfterBridge';

type Phase = 'before' | 'content' | 'after' | 'comparison';

interface Props {
  data: BeforeAfterBridgeSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: BeforeAfterBridgeSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

export function BeforeAfterBridgeSlide({
  data,
  theme,
  isEditing,
  onUpdate,
  onActivityResponse,
  onComplete,
  savedResponse,
}: Props) {
  if (isEditing) {
    return <BeforeAfterBridgeEditor data={data} theme={theme} onUpdate={onUpdate} />;
  }
  // Tier-A bespoke renderer for the "Aurora Drift" world (home-set pairing #2).
  // Fires onComplete once at the terminal after-phase submit; rehydrates from a
  // saved { before, after } payload straight to the comparison view.
  if (theme.id === 'aurora') {
    return (
      <AuroraBeforeAfterBridge
        data={data}
        theme={theme}
        onActivityResponse={onActivityResponse}
        onComplete={onComplete}
        savedResponse={savedResponse}
      />
    );
  }
  return (
    <BeforeAfterBridgeStudent
      data={data}
      theme={theme}
      onActivityResponse={onActivityResponse}
      onComplete={onComplete}
      savedResponse={savedResponse}
    />
  );
}

// ---------------------------------------------------------------------------
// Student-facing renderer
// ---------------------------------------------------------------------------

function BeforeAfterBridgeStudent({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: BeforeAfterBridgeSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const accent = theme.colors.accent || theme.colors.primary;

  // A saved {before, after} payload describes the finished end-state — restore the comparison view.
  const savedBefore = savedResponse?.before as Record<string, string> | undefined;
  const savedAfter = savedResponse?.after as Record<string, string> | undefined;
  const restored = !!(savedBefore && typeof savedBefore === 'object' && savedAfter && typeof savedAfter === 'object');

  // Scale sliders display a default of 3 even before the learner touches them,
  // so seed the recorded state with that default — display and state must never diverge.
  const seedScaleDefaults = (
    prompts: Array<{ id: string; inputType: 'text' | 'scale' }>,
  ): Record<string, string> =>
    Object.fromEntries(prompts.filter((p) => p.inputType === 'scale').map((p) => [p.id, '3']));

  const [phase, setPhase] = useState<Phase>(restored ? 'comparison' : 'before');
  const [before, setBefore] = useState<Record<string, string>>(() =>
    restored ? savedBefore! : seedScaleDefaults(data.beforePhase.prompts),
  );
  const [after, setAfter] = useState<Record<string, string>>(() =>
    restored
      ? savedAfter!
      : seedScaleDefaults(
          data.afterPhase.prompts.map((p) => ({
            id: p.id,
            inputType: data.beforePhase.prompts.find((bp) => bp.id === p.id)?.inputType ?? 'text',
          })),
        ),
  );
  const completedRef = useRef(restored);

  const hasPrompts = data.beforePhase.prompts.length > 0 || data.afterPhase.prompts.length > 0;
  const beforeReady = data.beforePhase.prompts.every((p) => (before[p.id] ?? '').trim().length > 0);
  const afterReady = data.afterPhase.prompts.every((p) => (after[p.id] ?? '').trim().length > 0);

  const submit = useCallback(() => {
    onActivityResponse?.({
      activityType: 'before-after-bridge',
      mode: 'survey',
      responseData: { before, after },
    });
    setPhase('comparison');
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [before, after, onActivityResponse, onComplete]);

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      <div className="mx-auto max-w-[720px] px-6 py-10">
        <header className="text-center mb-8">
          <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text, marginBottom: 8 }}>
            {data.title}
          </h1>
          {data.subtitle && (
            <p style={{ color: theme.colors.textMuted, fontSize: '0.95rem' }}>{data.subtitle}</p>
          )}
        </header>

        {!hasPrompts && (
          <div
            className="rounded-lg border p-6 text-center"
            style={{ background: theme.colors.surface, borderColor: `${accent}30` }}
          >
            <p className="text-sm" style={{ color: theme.colors.textMuted }}>
              This activity has no prompts yet — there is nothing to answer here.
            </p>
          </div>
        )}

        {hasPrompts && <PhaseTracker phase={phase} accent={accent} theme={theme} />}

        {hasPrompts && phase === 'before' && (
          <PromptList
            instruction={data.beforePhase.instruction}
            prompts={data.beforePhase.prompts.map((p) => ({
              id: p.id,
              text: p.beforeText,
              inputType: p.inputType,
              maxLength: p.maxLength,
              scaleLabels: p.scaleLabels,
            }))}
            values={before}
            onChange={(id, v) => setBefore((s) => ({ ...s, [id]: v }))}
            theme={theme}
            actionLabel="Continue"
            actionReady={beforeReady}
            onAction={() => setPhase('content')}
          />
        )}

        {hasPrompts && phase === 'content' && (
          <div className="space-y-4">
            <p className="text-sm" style={{ color: theme.colors.textMuted }}>
              {data.contentPhase.instruction}
            </p>
            {data.contentPhase.blocks.map((block) => (
              <ContentBlock key={block.id} block={block} theme={theme} accent={accent} />
            ))}
            <button
              onClick={() => setPhase('after')}
              className="w-full mt-4 px-6 py-3 rounded-lg font-semibold text-sm"
              style={{ background: accent, color: '#fff' }}
            >
              I&apos;ve read this — continue
            </button>
          </div>
        )}

        {hasPrompts && phase === 'after' && (
          <PromptList
            instruction={data.afterPhase.instruction}
            prompts={data.afterPhase.prompts.map((p) => {
              const beforeMatch = data.beforePhase.prompts.find((bp) => bp.id === p.id);
              return {
                id: p.id,
                text: p.afterText,
                inputType: beforeMatch?.inputType ?? 'text',
                maxLength: beforeMatch?.maxLength,
                scaleLabels: beforeMatch?.scaleLabels,
                hint: before[p.id] ? `You said before: "${before[p.id]}"` : undefined,
              };
            })}
            values={after}
            onChange={(id, v) => setAfter((s) => ({ ...s, [id]: v }))}
            theme={theme}
            actionLabel="See my shift"
            actionReady={afterReady}
            onAction={submit}
          />
        )}

        <AnimatePresence>
          {hasPrompts && phase === 'comparison' && (
            <Reveal variant="up">
              <ComparisonView
                data={data}
                before={before}
                after={after}
                theme={theme}
                accent={accent}
              />
            </Reveal>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function PhaseTracker({ phase, accent, theme }: { phase: Phase; accent: string; theme: Theme }) {
  const phases: { key: Phase; label: string }[] = [
    { key: 'before', label: 'Before' },
    { key: 'content', label: 'Read' },
    { key: 'after', label: 'After' },
    { key: 'comparison', label: 'Compare' },
  ];
  const order = phases.map((p) => p.key);
  const currentIdx = order.indexOf(phase);
  return (
    <div className="flex items-center gap-2 mb-8 text-xs">
      {phases.map((p, idx) => {
        const isDone = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        return (
          <div key={p.key} className="flex items-center gap-2 flex-1">
            <span
              className="inline-flex items-center justify-center px-3 py-1.5 rounded-full font-semibold transition-colors duration-300"
              style={{
                background: isCurrent ? accent : isDone ? `${accent}25` : theme.colors.surface,
                color: isCurrent ? '#fff' : isDone ? accent : theme.colors.textMuted,
              }}
            >
              {p.label}
            </span>
            {idx < phases.length - 1 && (
              <div
                className="flex-1 h-0.5 rounded transition-colors duration-300"
                style={{ background: isDone ? accent : `${theme.colors.textMuted}33` }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

interface PromptViewModel {
  id: string;
  text: string;
  inputType: 'text' | 'scale';
  maxLength?: number;
  scaleLabels?: [string, string];
  hint?: string;
}

function PromptList({
  instruction,
  prompts,
  values,
  onChange,
  theme,
  actionLabel,
  actionReady,
  onAction,
}: {
  instruction: string;
  prompts: PromptViewModel[];
  values: Record<string, string>;
  onChange: (id: string, value: string) => void;
  theme: Theme;
  actionLabel: string;
  actionReady: boolean;
  onAction: () => void;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  return (
    <div className="space-y-4">
      {instruction && (
        <p className="text-sm" style={{ color: theme.colors.textMuted }}>
          {instruction}
        </p>
      )}
      {prompts.map((p) => (
        <div
          key={p.id}
          className="rounded-lg p-5 border"
          style={{ background: theme.colors.surface, borderColor: `${accent}30` }}
        >
          <label className="block text-sm font-semibold mb-3" style={{ color: theme.colors.text }}>
            {p.text}
          </label>
          {p.hint && (
            <p
              className="text-xs italic mb-3 px-3 py-2 rounded"
              style={{ background: `${accent}15`, color: theme.colors.textMuted }}
            >
              {p.hint}
            </p>
          )}
          {p.inputType === 'scale' ? (
            <ScaleInput
              value={values[p.id] ?? ''}
              labels={p.scaleLabels ?? ['Low', 'High']}
              onChange={(v) => onChange(p.id, v)}
              theme={theme}
            />
          ) : (
            <>
              <textarea
                value={values[p.id] ?? ''}
                onChange={(e) => onChange(p.id, e.target.value)}
                maxLength={p.maxLength ?? 300}
                rows={3}
                className="w-full px-3 py-2 rounded border text-sm"
                style={{
                  background: theme.colors.background,
                  borderColor: `${theme.colors.textMuted}33`,
                  color: theme.colors.text,
                }}
              />
              <div className="text-right text-xs mt-1" style={{ color: theme.colors.textMuted }}>
                {(values[p.id] ?? '').length} / {p.maxLength ?? 300}
              </div>
            </>
          )}
        </div>
      ))}
      <button
        onClick={onAction}
        disabled={!actionReady}
        className="w-full px-6 py-3 rounded-lg font-semibold text-sm"
        style={{
          background: accent,
          color: '#fff',
          cursor: actionReady ? 'pointer' : 'not-allowed',
          opacity: actionReady ? 1 : 0.4,
        }}
      >
        {actionLabel}
      </button>
    </div>
  );
}

function ScaleInput({
  value,
  labels,
  onChange,
  theme,
}: {
  value: string;
  labels: [string, string];
  onChange: (v: string) => void;
  theme: Theme;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const numericValue = Number(value) || 3;
  const pct = ((numericValue - 1) / 4) * 100;
  return (
    <div>
      <div className="flex justify-between text-xs mb-2" style={{ color: theme.colors.textMuted }}>
        <span>{labels[0]}</span>
        <span>{labels[1]}</span>
      </div>
      <input
        type="range"
        min={1}
        max={5}
        value={numericValue}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-2 rounded-lg appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, ${accent} 0%, ${accent} ${pct}%, ${theme.colors.textMuted}33 ${pct}%, ${theme.colors.textMuted}33 100%)`,
        }}
      />
      <div className="text-center text-2xl font-bold mt-2" style={{ color: accent }}>
        {numericValue}
      </div>
    </div>
  );
}

function ContentBlock({
  block,
  theme,
  accent,
}: {
  block: BeforeAfterBridgeSlideData['contentPhase']['blocks'][number];
  theme: Theme;
  accent: string;
}) {
  if (block.type === 'quote') {
    return (
      <blockquote
        className="border-l-4 pl-4 py-2"
        style={{ borderColor: accent }}
      >
        <p className="italic" style={{ color: theme.colors.text }}>
          “{block.content}”
        </p>
        {block.source && (
          <p className="text-xs mt-2" style={{ color: theme.colors.textMuted }}>
            — {block.source}
          </p>
        )}
      </blockquote>
    );
  }
  return (
    <div
      className="rounded-lg p-5"
      style={{
        background: block.type === 'data' ? `${accent}10` : theme.colors.surface,
        border: block.type === 'data' ? `1px solid ${accent}30` : 'none',
      }}
    >
      <p className="leading-relaxed" style={{ color: theme.colors.text }}>
        {block.content}
      </p>
      {block.source && (
        <p className="text-xs mt-2" style={{ color: theme.colors.textMuted }}>
          {block.source}
        </p>
      )}
    </div>
  );
}

function ComparisonView({
  data,
  before,
  after,
  theme,
  accent,
}: {
  data: BeforeAfterBridgeSlideData;
  before: Record<string, string>;
  after: Record<string, string>;
  theme: Theme;
  accent: string;
}) {
  // Tier 2 reflect-back: pull the first rating (scale) prompt's before/after so
  // the close can say "your rating moved {before} → {after}" from real values.
  // Text-only experiences have no numeric delta — the default body then reads
  // without the numbers (fillSlots strips the unfilled tokens).
  const scalePrompt = data.beforePhase.prompts.find((p) => p.inputType === 'scale');
  const beforeNum = scalePrompt ? Number(before[scalePrompt.id]) : NaN;
  const afterNum = scalePrompt ? Number(after[scalePrompt.id]) : NaN;
  const hasDelta = !Number.isNaN(beforeNum) && !Number.isNaN(afterNum);
  const values = hasDelta
    ? { before: beforeNum, after: afterNum, delta: afterNum - beforeNum }
    : {};

  // Generic default = bare fact only (no homily — a content-blind line can't
  // earn insight, so it doesn't pretend to). The real voice comes from an
  // authored `data.conclusion`. No dashes (normalizeProse enforces it anyway).
  const conclusion: ExperienceConclusion = data.conclusion ?? {
    heading: 'So what',
    body: hasDelta
      ? 'Your rating moved {before} → {after}.'
      : 'Look back at your before and after answers above.',
    reflectionPrompt: data.comparison?.reflectionPrompt,
  };

  return (
    <div className="space-y-4">
      <h2 style={{ ...slideTypographyStyle(theme, 'subheading'), color: accent, fontWeight: 700 }}>
        Your shift
      </h2>
      {data.beforePhase.prompts.map((bp, idx) => {
        const ap = data.afterPhase.prompts.find((p) => p.id === bp.id);
        if (!ap) return null;
        const beforeVal = before[bp.id] ?? '';
        const afterVal = after[bp.id] ?? '';
        return (
          <div
            key={bp.id}
            className="rounded-xl p-5 border shadow-sm"
            style={{ background: theme.colors.surface, borderColor: `${accent}30` }}
          >
            <p className="text-xs uppercase tracking-wider mb-2" style={{ color: theme.colors.textMuted }}>
              {bp.beforeText}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded" style={{ background: theme.colors.background }}>
                <p className="text-xs font-bold mb-1" style={{ color: theme.colors.textMuted }}>BEFORE</p>
                <p className="text-sm" style={{ color: theme.colors.text }}>{beforeVal || '—'}</p>
              </div>
              {/* AFTER cell staggers in after the row mounts — the "shift" lands */}
              <Reveal variant="up" delay={0.15 + idx * 0.1} className="p-3 rounded" style={{ background: `${accent}15` }}>
                <p className="text-xs font-bold mb-1" style={{ color: accent }}>AFTER</p>
                <p className="text-sm" style={{ color: theme.colors.text }}>{afterVal || '—'}</p>
              </Reveal>
            </div>
          </div>
        );
      })}
      {/* Orphan after-prompts (no before twin) still carry a captured answer — show them with an empty before side. */}
      {data.afterPhase.prompts
        .filter((ap) => !data.beforePhase.prompts.some((bp) => bp.id === ap.id))
        .map((ap) => {
          const afterVal = after[ap.id] ?? '';
          return (
            <div
              key={ap.id}
              className="rounded-xl p-5 border shadow-sm"
              style={{ background: theme.colors.surface, borderColor: `${accent}30` }}
            >
              <p className="text-xs uppercase tracking-wider mb-2" style={{ color: theme.colors.textMuted }}>
                {ap.afterText}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded" style={{ background: theme.colors.background }}>
                  <p className="text-xs font-bold mb-1" style={{ color: theme.colors.textMuted }}>BEFORE</p>
                  <p className="text-sm" style={{ color: theme.colors.text }}>—</p>
                </div>
                <div className="p-3 rounded" style={{ background: `${accent}15` }}>
                  <p className="text-xs font-bold mb-1" style={{ color: accent }}>AFTER</p>
                  <p className="text-sm" style={{ color: theme.colors.text }}>{afterVal || '—'}</p>
                </div>
              </div>
            </div>
          );
        })}
      {/* Authored "So what?" close — replaces the old dead-end Reflect prompt.
          Leans on the captured rating shift (system-actionable), folds the legacy
          reflectionPrompt into the close's optional reflection line, and falls
          back to a built-in reflect-back when no `conclusion` is authored yet. */}
      <div className="mt-6">
        <ExperienceClose
          conclusion={conclusion}
          theme={theme}
          values={values}
          fallback="Look back at your before and after answers above."
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editing-mode renderer
// ---------------------------------------------------------------------------

function BeforeAfterBridgeEditor({
  data,
  theme,
  onUpdate,
}: {
  data: BeforeAfterBridgeSlideData;
  theme: Theme;
  onUpdate: (data: BeforeAfterBridgeSlideData) => void;
}) {
  const accent = theme.colors.accent || theme.colors.primary;

  const updateField = <K extends keyof BeforeAfterBridgeSlideData>(
    key: K,
    value: BeforeAfterBridgeSlideData[K],
  ) => onUpdate({ ...data, [key]: value });

  // Before/after prompts are paired by id. Adding a prompt creates the pair;
  // removing removes both halves.
  const addPromptPair = () => {
    const newId = crypto.randomUUID();
    onUpdate({
      ...data,
      beforePhase: {
        ...data.beforePhase,
        prompts: [
          ...data.beforePhase.prompts,
          { id: newId, beforeText: '', inputType: 'text', maxLength: 300 },
        ],
      },
      afterPhase: {
        ...data.afterPhase,
        prompts: [...data.afterPhase.prompts, { id: newId, afterText: '' }],
      },
    });
  };

  const removePromptPair = (promptId: string) => {
    onUpdate({
      ...data,
      beforePhase: {
        ...data.beforePhase,
        prompts: data.beforePhase.prompts.filter((p) => p.id !== promptId),
      },
      afterPhase: {
        ...data.afterPhase,
        prompts: data.afterPhase.prompts.filter((p) => p.id !== promptId),
      },
    });
  };

  const updateBeforePrompt = (
    promptId: string,
    patch: Partial<BeforeAfterBridgeSlideData['beforePhase']['prompts'][number]>,
  ) =>
    onUpdate({
      ...data,
      beforePhase: {
        ...data.beforePhase,
        prompts: data.beforePhase.prompts.map((p) => (p.id === promptId ? { ...p, ...patch } : p)),
      },
    });

  const updateAfterPrompt = (promptId: string, afterText: string) =>
    onUpdate({
      ...data,
      afterPhase: {
        ...data.afterPhase,
        prompts: data.afterPhase.prompts.map((p) => (p.id === promptId ? { ...p, afterText } : p)),
      },
    });

  const addBlock = () =>
    onUpdate({
      ...data,
      contentPhase: {
        ...data.contentPhase,
        blocks: [
          ...data.contentPhase.blocks,
          { id: crypto.randomUUID(), type: 'text', content: '' },
        ],
      },
    });

  const updateBlock = (id: string, patch: Partial<BeforeAfterBridgeSlideData['contentPhase']['blocks'][number]>) =>
    onUpdate({
      ...data,
      contentPhase: {
        ...data.contentPhase,
        blocks: data.contentPhase.blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)),
      },
    });

  const removeBlock = (id: string) =>
    onUpdate({
      ...data,
      contentPhase: {
        ...data.contentPhase,
        blocks: data.contentPhase.blocks.filter((b) => b.id !== id),
      },
    });

  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[720px]">
        <input
          value={data.title}
          onChange={(e) => updateField('title', e.target.value)}
          placeholder="Before/After title…"
          className="w-full mb-2 bg-transparent border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none transition-colors"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}
        />
        <input
          value={data.subtitle}
          onChange={(e) => updateField('subtitle', e.target.value)}
          placeholder="Subtitle"
          className="w-full mb-6 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm transition-colors"
          style={{ color: theme.colors.textMuted }}
        />

        {/* Before phase */}
        <Section title="1. Before — surface assumptions" accent={accent} theme={theme}>
          <textarea
            value={data.beforePhase.instruction}
            onChange={(e) =>
              updateField('beforePhase', { ...data.beforePhase, instruction: e.target.value })
            }
            placeholder="Instruction for the before phase"
            rows={2}
            className="w-full mb-3 px-3 py-2 rounded border text-sm"
            style={{
              background: theme.colors.background,
              borderColor: `${theme.colors.textMuted}33`,
              color: theme.colors.text,
            }}
          />
          {data.beforePhase.prompts.map((bp, idx) => {
            const afterPrompt = data.afterPhase.prompts.find((p) => p.id === bp.id);
            return (
              <div
                key={bp.id}
                className="rounded p-3 mb-2"
                style={{ background: theme.colors.background, border: `1px solid ${theme.colors.textMuted}22` }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold" style={{ color: accent }}>
                    Pair {idx + 1}
                  </span>
                  <div className="flex items-center gap-2">
                    <select
                      value={bp.inputType}
                      onChange={(e) =>
                        updateBeforePrompt(bp.id, {
                          inputType: e.target.value as 'text' | 'scale',
                        })
                      }
                      className="text-xs px-2 py-1 rounded border"
                      style={{
                        background: theme.colors.background,
                        borderColor: `${theme.colors.textMuted}33`,
                        color: theme.colors.text,
                      }}
                    >
                      <option value="text">Text</option>
                      <option value="scale">Scale</option>
                    </select>
                    <button
                      onClick={() => removePromptPair(bp.id)}
                      className="text-xs text-gray-400 hover:text-red-500"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <input
                  value={bp.beforeText}
                  onChange={(e) => updateBeforePrompt(bp.id, { beforeText: e.target.value })}
                  placeholder="Before prompt"
                  className="w-full mb-2 px-2 py-1 rounded border text-sm"
                  style={{
                    background: theme.colors.surface,
                    borderColor: `${theme.colors.textMuted}33`,
                    color: theme.colors.text,
                  }}
                />
                <input
                  value={afterPrompt?.afterText ?? ''}
                  onChange={(e) => updateAfterPrompt(bp.id, e.target.value)}
                  placeholder="After prompt (reworded)"
                  className="w-full px-2 py-1 rounded border text-sm"
                  style={{
                    background: theme.colors.surface,
                    borderColor: `${theme.colors.textMuted}33`,
                    color: theme.colors.text,
                  }}
                />
                {bp.inputType === 'scale' && (
                  <div className="flex gap-2 mt-2">
                    <input
                      value={bp.scaleLabels?.[0] ?? ''}
                      onChange={(e) =>
                        updateBeforePrompt(bp.id, {
                          scaleLabels: [
                            e.target.value,
                            bp.scaleLabels?.[1] ?? '',
                          ],
                        })
                      }
                      placeholder="Low label"
                      className="flex-1 px-2 py-1 rounded border text-xs"
                      style={{
                        background: theme.colors.surface,
                        borderColor: `${theme.colors.textMuted}33`,
                        color: theme.colors.text,
                      }}
                    />
                    <input
                      value={bp.scaleLabels?.[1] ?? ''}
                      onChange={(e) =>
                        updateBeforePrompt(bp.id, {
                          scaleLabels: [bp.scaleLabels?.[0] ?? '', e.target.value],
                        })
                      }
                      placeholder="High label"
                      className="flex-1 px-2 py-1 rounded border text-xs"
                      style={{
                        background: theme.colors.surface,
                        borderColor: `${theme.colors.textMuted}33`,
                        color: theme.colors.text,
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
          <button onClick={addPromptPair} className="text-xs" style={{ color: accent }}>
            + Add prompt pair
          </button>
        </Section>

        {/* Content phase */}
        <Section title="2. Content — what shifts the view" accent={accent} theme={theme}>
          <textarea
            value={data.contentPhase.instruction}
            onChange={(e) =>
              updateField('contentPhase', { ...data.contentPhase, instruction: e.target.value })
            }
            placeholder="Instruction for the reading phase"
            rows={2}
            className="w-full mb-3 px-3 py-2 rounded border text-sm"
            style={{
              background: theme.colors.background,
              borderColor: `${theme.colors.textMuted}33`,
              color: theme.colors.text,
            }}
          />
          {data.contentPhase.blocks.map((block, idx) => (
            <div
              key={block.id}
              className="rounded p-3 mb-2"
              style={{ background: theme.colors.background, border: `1px solid ${theme.colors.textMuted}22` }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold" style={{ color: accent }}>
                  Block {idx + 1}
                </span>
                <div className="flex items-center gap-2">
                  <select
                    value={block.type}
                    onChange={(e) =>
                      updateBlock(block.id, { type: e.target.value as 'text' | 'data' | 'quote' })
                    }
                    className="text-xs px-2 py-1 rounded border"
                    style={{
                      background: theme.colors.background,
                      borderColor: `${theme.colors.textMuted}33`,
                      color: theme.colors.text,
                    }}
                  >
                    <option value="text">Text</option>
                    <option value="data">Data</option>
                    <option value="quote">Quote</option>
                  </select>
                  <button onClick={() => removeBlock(block.id)} className="text-xs text-gray-400 hover:text-red-500">
                    ×
                  </button>
                </div>
              </div>
              <textarea
                value={block.content}
                onChange={(e) => updateBlock(block.id, { content: e.target.value })}
                placeholder="Content"
                rows={3}
                className="w-full mb-2 px-2 py-1 rounded border text-sm"
                style={{
                  background: theme.colors.surface,
                  borderColor: `${theme.colors.textMuted}33`,
                  color: theme.colors.text,
                }}
              />
              <input
                value={block.source ?? ''}
                onChange={(e) => updateBlock(block.id, { source: e.target.value })}
                placeholder="Source (optional)"
                className="w-full px-2 py-1 rounded border text-xs"
                style={{
                  background: theme.colors.surface,
                  borderColor: `${theme.colors.textMuted}33`,
                  color: theme.colors.text,
                }}
              />
            </div>
          ))}
          <button onClick={addBlock} className="text-xs" style={{ color: accent }}>
            + Add content block
          </button>
        </Section>

        <Section title="3. Reflection prompt" accent={accent} theme={theme}>
          <textarea
            value={data.comparison?.reflectionPrompt ?? ''}
            onChange={(e) =>
              updateField('comparison', { reflectionPrompt: e.target.value })
            }
            placeholder="What surprised you about your shift?"
            rows={2}
            className="w-full px-3 py-2 rounded border text-sm"
            style={{
              background: theme.colors.background,
              borderColor: `${theme.colors.textMuted}33`,
              color: theme.colors.text,
            }}
          />
        </Section>
      </div>
    </div>
  );
}

function Section({
  title,
  accent,
  theme,
  children,
}: {
  title: string;
  accent: string;
  theme: Theme;
  children: React.ReactNode;
}) {
  return (
    <div
      className="rounded-lg border p-5 mb-4"
      style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}
    >
      <h3 className="text-xs font-bold tracking-wider uppercase mb-3" style={{ color: accent }}>
        {title}
      </h3>
      {children}
    </div>
  );
}
